import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { useSelector, useDispatch } from 'react-redux';
import {
  Route,
  Switch,
  useHistory,
  useRouteMatch,
  useLocation,
} from 'react-router';
import {
  duplicateMatrix,
  convertExperimentToMatrix as convertToMatrix,
} from 'constants/matrices';
import MatrixErrors from 'components/MatrixErrors';
import {
  Menu,
  MenuActions,
  MatrixMenuName,
  MenuSection,
  MenuText,
} from './Menu';
import './MatrixMenu.scss';
import { useCanEdit } from 'hooks/matrices';
import Popover from '../Popover';
import Icon from '../Icon';
import ButtonNew from 'components/inputs/ButtonNew';
import classNames from 'classnames';
import { useNewExperiment } from 'hooks/experiments';
import { finishEdit } from 'actions/matrixSettings';
import {
  createMatrix,
  saveMatrix,
  convertExperimentToMatrix,
  removeMatrix,
  fetchMatrix,
} from 'actions/matrices';
import { Tooltip } from 'react-tooltip';
import { useIsInUse } from 'hooks/matrices';
import Checkbox from 'components/inputs/Checkbox';
import { compareMatrices } from 'constants/matrices';
import { OverridesListElement, OverrideElement } from 'components/Overrides';
import { nodeSortFunction } from 'constants/nodes';
import { ownerPermissions, writePermissions } from 'constants/auth';

const GenericPopoverContent = ({
  text,
  additionalContent,
  primaryAction,
  primaryActionLabel,
  secondaryAction,
  secondaryActionLabel,
  checkboxAction,
  checkboxActionValue,
  checkboxActionLabel,
}) => {
  return (
    <div className="MatrixMenu-GenericPopoverContent">
      <div className="Popover-section">{text}</div>

      <div className="Popover-section">
        {checkboxAction ? (
          <Checkbox
            label={checkboxActionLabel}
            value={checkboxActionValue}
            className="tour-checkbox-carryover-experiment"
            id="usePlayMode"
            onChange={checkboxAction}
          />
        ) : null}
      </div>

      {additionalContent ? (
        <div className="Popover-section">{additionalContent}</div>
      ) : null}

      <div className="MatrixMenu-GenericPopoverContent-buttons-container">
        <ButtonNew
          buttonClasses="secondary-button-dark-small"
          onClick={secondaryAction}
          label={secondaryActionLabel}
        />

        <ButtonNew
          label={primaryActionLabel}
          onClick={primaryAction}
          buttonClasses="primary-button-small tour-create-experiment"
        />
      </div>
    </div>
  );
};

const SharePopoverContent = ({ url, onCancel, noEdit, activeMatrix }) => {
  const [linkIcon, setLinkIcon] = useState('link');
  const [editableLinkIcon, setEditableLinkIcon] = useState('link');
  const { user } = useSelector(state => state.auth);

  const isExperiment = useMemo(() => {
    return !!activeMatrix?.parentId;
  }, [activeMatrix]);

  const isOwner = useMemo(() => {
    const userId = user?.claims?.user_id;
    return ownerPermissions.includes(activeMatrix?.roles?.[userId]);
  }, [user, activeMatrix]);

  const setTemporaryIconChange = useCallback(type => {
    let fn;
    if (type === 'editable') {
      fn = setEditableLinkIcon;
    } else {
      fn = setLinkIcon;
    }
    fn('check');
    setTimeout(() => fn('link'), 1000);
  }, []);

  const copyUrlToClipboard = useCallback(
    (address, type) => {
      navigator.clipboard.writeText(address);
      setTemporaryIconChange(type);
    },
    [setTemporaryIconChange]
  );

  const matrixOrExperiment = isExperiment ? 'experiment' : 'matrix';

  const shareModalText = noEdit
    ? `Copy a link of this ${matrixOrExperiment} for anyone to view.`
    : `Copy a link of this ${matrixOrExperiment} for anyone to view. Any changes you save to
  this ${matrixOrExperiment} will update in this link.`;

  const anonymousEditingKey = useSelector(state => state.anonymousEditingKeys);
  const editingKey = anonymousEditingKey?.keys[activeMatrix.id];

  const hasKey = !!editingKey;

  const editableUrl = useMemo(() => {
    return `${url}/key/${editingKey}`;
  }, [url, editingKey]);

  return (
    <div className="MatrixMenu-GenericPopoverContent">
      <div className="Popover-section">{shareModalText}</div>
      <div className="Popover-section">
        {hasKey ? (
          <div className="Popover-link-header">Viewable only</div>
        ) : null}
        <div
          className="Popover-link"
          onClick={() => copyUrlToClipboard(url, 'viewable')}
        >
          <div className="Popover-link-text">{url}</div>
          <Icon
            alt="shareLink"
            icon={linkIcon}
            className="Popover-link-icon icon-white"
          />
        </div>
      </div>

      {hasKey ? (
        <div className="Popover-section">
          <div className="Popover-link-header">Editable</div>
          <div
            className="Popover-link"
            onClick={() => copyUrlToClipboard(editableUrl, 'editable')}
          >
            <div className="Popover-link-text">{editableUrl}</div>
            <Icon
              alt="shareLink"
              icon={editableLinkIcon}
              className="Popover-link-icon icon-white"
            />
          </div>
        </div>
      ) : null}

      {isOwner ? (
        <div className="Popover-section">
          <div className="Popover-text">
            To give another user edit access to your matrix, go to the Edit
            Access tab in <a href={`${url}/edit`}>Matrix Settings</a>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const MatrixMenu = ({ matrix, viewMode }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const getNewExperiment = useNewExperiment();
  let { url } = useRouteMatch();

  const isExperiment = useMemo(() => !!matrix?.parentId, [matrix]);

  const { user } = useSelector(state => state.auth);
  const { user_id } = user?.claims ?? { user_id: null };
  const saving = useSelector(state => state.matrices.saving);
  const [isSaving, setIsSaving] = useState(false);

  const nodeOverrides = useSelector(
    state => state.matrixSettings.playModeNodeOverrides
  );

  // Keep badge up for 3 seconds on save
  useEffect(() => {
    if (saving) {
      setIsSaving(true);
      setTimeout(() => setIsSaving(false), 3000);
    }
  }, [saving]);

  const shareRef = useRef(null);
  const duplicateRef = useRef(null);
  const experimentRef = useRef(null);
  const convertRef = useRef(null);

  const initialPopoverState = {
    share: false,
    duplicate: false,
    experiment: false,
    convert: false,
  };

  const [openPopovers, setOpenPopovers] = useState(initialPopoverState);

  const switchPopovers = useCallback((key, value) => {
    setOpenPopovers({
      ...initialPopoverState,
      [key]: value,
    });
  }, []);

  const [
    createExperimentUseNodeOverrides,
    setCreateExperimentUseNodeOverrides,
  ] = useState(false);

  useEffect(() => {
    if (openPopovers?.experiment === false) {
      setCreateExperimentUseNodeOverrides(false);
    }
  }, [openPopovers]);

  const { errors } = useSelector(state => state.matrices);

  const canEdit = useCanEdit();

  const hasEditAccess = useMemo(
    () => writePermissions.includes(matrix?.roles?.[user_id]),
    [matrix, user_id]
  );
  const isInUse = useIsInUse({ id: matrix?.id, hasEditAccess });

  const matrixIsInUse = useMemo(() => {
    return (
      hasEditAccess &&
      !!isInUse &&
      isInUse?.user !== user_id &&
      isInUse?.user !== null
    );
  }, [user_id, isInUse, hasEditAccess]);

  const editable = useMemo(() => canEdit(matrix) && !matrixIsInUse, [
    matrixIsInUse,
    matrix,
    canEdit,
  ]);

  // This is the matrix if play mode overrides are in place
  const overriddenMatrix = useMemo(() => {
    const overriddenNodes = Object.fromEntries(
      Object.entries({ ...matrix?.nodes }).map(([id, node]) => {
        const nextNode = nodeOverrides.find(n => n.id === id) ?? node;
        return [id, nextNode];
      })
    );
    const overridden = { ...matrix, nodes: overriddenNodes };
    return overridden;
  }, [matrix, nodeOverrides]);

  const onCreateExperiment = useCallback(() => {
    const baseForExperiment = createExperimentUseNodeOverrides
      ? overriddenMatrix
      : matrix;

    const newExperiment = getNewExperiment(baseForExperiment);

    const { parentId, id } = newExperiment;
    // We have the matrix, but fetch anyway since it's all
    // saved and hasn't hit the loading list
    dispatch(fetchMatrix(id));
    history.push(`/matrices/${parentId}/experiments/${id}/edit`);
  }, [
    dispatch,
    getNewExperiment,
    overriddenMatrix,
    createExperimentUseNodeOverrides,
    history,
    matrix,
  ]);

  const onDuplicateMatrix = useCallback(() => {
    const toDuplicate = matrix;
    const duplicate = duplicateMatrix(toDuplicate, user);
    dispatch(finishEdit(toDuplicate));
    dispatch(createMatrix(duplicate));
    dispatch(saveMatrix(duplicate.id));

    const urlPath = matrix?.parentId
      ? `/matrices/${duplicate?.parentId}/experiments/${duplicate?.id}/edit`
      : `/matrices/${duplicate?.id}/edit`;

    // We have the matrix, but fetch anyway since it's all
    // saved and hasn't hit the loading list
    dispatch(fetchMatrix(duplicate.id));

    history.push(urlPath);
  }, [dispatch, history, user, matrix]);

  const handleConvertToMatrix = useCallback(() => {
    if (!matrix?.parentId) return;
    const newMatrix = convertToMatrix(matrix, user);
    dispatch(finishEdit(matrix));
    dispatch(convertExperimentToMatrix(newMatrix));
    dispatch(saveMatrix(newMatrix.id));

    history.push(`/matrices/${newMatrix?.id}/edit`);

    if (matrix?.parentId) {
      dispatch(removeMatrix(matrix.id));
    }
  }, [history, dispatch, user, matrix]);

  const isViewOnly = useMemo(() => {
    return !!matrix?.isViewOnly;
  }, [matrix]);

  const downloadDetails = useCallback(async () => {
    const detailsPage = document.getElementById('details-page');
    detailsPage.classList.add('for-image-download');

    const fileName = `${matrix.name}-details`;
    const charts = document.getElementById('charts-download');

    const imageEls = document.getElementsByClassName('NodePill-icon');

    const originalHrefs = [];

    // Convert svg image tags to base64 due to html2canvas bug
    // See https://github.com/niklasvh/html2canvas/issues/2104
    for (const imageEl of imageEls) {
      const hrefUrl = imageEl?.href?.baseVal;
      originalHrefs.push(hrefUrl);

      const svgStr = await fetch(
        `${window.location.origin}${hrefUrl}`
      ).then(response => response.text());

      const base64Str = window.btoa(svgStr);

      imageEl.removeAttribute('href');

      imageEl.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'xlink:href',
        `data:image/svg+xml;base64,${base64Str}`
      );
    }

    html2canvas(charts).then(canvas => {
      saveAs(canvas.toDataURL(), `${fileName}.png`);
    });

    // Cleanup back to original
    for (let i = 0; i < imageEls.length; i++) {
      const imageEl = imageEls[i];
      imageEl.removeAttribute('xlink:href');

      imageEl.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'href',
        originalHrefs[i]
      );
    }

    detailsPage.classList.remove('for-image-download');
  }, []);

  const { pathname } = useLocation();

  const location = useMemo(() => {
    const path = pathname.replace(url, '');
    return path;
  }, [url, pathname]);

  useEffect(() => {
    const editingOpen =
      location.includes('edit') || location.includes('overrides');
    if (editingOpen && Object.values(openPopovers).some(v => !!v)) {
      setOpenPopovers(initialPopoverState);
    }
  }, [location, initialPopoverState, openPopovers]);

  const nodeOverrideElements = useMemo(() => {
    if (!matrix || !overriddenMatrix) return null;

    const overrides = compareMatrices(matrix, overriddenMatrix);

    if (Object.values(overrides).every(v => !v.length)) return null;

    const nodesSorted = Object.values({ ...matrix?.nodes })
      .filter(node => overrides[node.id].length > 0)
      .sort((a, b) => nodeSortFunction(a, b));

    return nodesSorted.map(node => (
      <OverridesListElement
        key={node.id}
        editable={false}
        node={node}
        overrides={overrides[node.id]}
        theme="dark"
      >
        {overrides[node.id].map((override, i) => (
          <OverrideElement
            key={i}
            editable={false}
            override={override}
            experiment={overriddenMatrix}
            matrix={matrix}
            theme="dark"
          />
        ))}
      </OverridesListElement>
    ));
  }, [matrix, overriddenMatrix]);

  const nodeOverrideProps = useMemo(() => {
    if (!nodeOverrides || !nodeOverrides.length) return {};
    return {
      checkboxAction: () => setCreateExperimentUseNodeOverrides(v => !v),
      checkboxActionValue: createExperimentUseNodeOverrides,
      checkboxActionLabel: 'Copy input changes to new experiment',
      additionalContent: createExperimentUseNodeOverrides
        ? nodeOverrideElements
        : null,
    };
  }, [nodeOverrides, createExperimentUseNodeOverrides, nodeOverrideElements]);

  return (
    <>
      <Menu className="MatrixMenu">
        <MenuSection>
          <div className="MatrixMenu-name-container">
            <MatrixMenuName
              matrix={matrix}
              children={
                <ButtonNew
                  buttonClasses={classNames(
                    'secondary-button-small MatrixMenu-gear-button'
                  )}
                  label="About this Matrix"
                  to={`${url}/edit`}
                />
              }
            />
          </div>

          {matrixIsInUse ? (
            <div className="MatrixMenu-save-warning">
              Note: Another user also has {matrix?.name} open. Any changes will
              not be saved.
            </div>
          ) : null}

          <div className="MatrixMenu-buttons-container">
            {/* Sorry for the nested ternary */}
            {isSaving ? (
              <div className="MatrixMenu-saving-badge">
                <Icon alt="check" icon="check" className="icon-green" /> New
                changes autosaved
              </div>
            ) : viewMode === 'edit' ? (
              <div className="MatrixMenu-editing-badge">
                Editing {isExperiment ? 'experiment' : 'matrix'}
              </div>
            ) : null}

            {isViewOnly ? (
              <div className="MatrixMenu-editing-badge">View-only</div>
            ) : null}

            {user && editable && isExperiment ? (
              <div
                ref={convertRef}
                className="MatrixMenu-button"
                data-tooltip-id="menu-tooltip"
                data-tooltip-content="Convert experiment to matrix"
                data-tooltip-place="bottom"
              >
                <ButtonNew
                  buttonClasses={classNames('icon-button', {
                    selected: openPopovers['convert'],
                  })}
                  onClick={() =>
                    switchPopovers('convert', !openPopovers['convert'])
                  }
                  icon="convert"
                />
              </div>
            ) : null}
            {user ? (
              <div
                ref={duplicateRef}
                className="MatrixMenu-button"
                data-tooltip-id="menu-tooltip"
                data-tooltip-content="Duplicate matrix"
                data-tooltip-place="bottom"
              >
                <ButtonNew
                  buttonClasses={classNames('icon-button', {
                    selected: openPopovers['duplicate'],
                  })}
                  onClick={() =>
                    switchPopovers('duplicate', !openPopovers['duplicate'])
                  }
                  icon="duplicateMatrix"
                />
              </div>
            ) : null}
            <div
              ref={shareRef}
              className="MatrixMenu-button"
              data-tooltip-id="menu-tooltip"
              data-tooltip-content="Share matrix"
              data-tooltip-place="bottom"
            >
              <ButtonNew
                buttonClasses={classNames('ShareButton icon-button', {
                  selected: openPopovers['share'],
                })}
                onClick={() => switchPopovers('share', !openPopovers['share'])}
                icon="link"
              />
            </div>
            <MenuActions>
              <Switch>
                <Route path={`${url}/details`}>
                  <ButtonNew
                    buttonClasses="primary-button-small"
                    label="Download"
                    onClick={downloadDetails}
                  />
                </Route>
                <Route>
                  <div ref={experimentRef}>
                    <ButtonNew
                      buttonClasses="CreateExperiment primary-button-small interactiveTourGettingStarted-9"
                      label="Create experiment"
                      onClick={() =>
                        switchPopovers(
                          'experiment',
                          !openPopovers['experiment']
                        )
                      }
                    />
                  </div>
                </Route>
              </Switch>
            </MenuActions>
          </div>
        </MenuSection>

        {errors.length > 0 ? <MatrixErrors /> : null}
        <Popover
          title={`Share ${isExperiment ? 'experiment' : 'matrix'}`}
          content={
            <SharePopoverContent
              noEdit={!editable || isViewOnly}
              url={`${window.location.origin}/#${url}`}
              onCancel={() => setOpenPopovers(v => ({ ...v, share: false }))}
              activeMatrix={matrix}
            />
          }
          isOpen={openPopovers['share']}
          parentRef={shareRef.current}
        />
        <Popover
          title={`Duplicate ${isExperiment ? 'experiment' : 'matrix'}`}
          content={
            <GenericPopoverContent
              text={`Make an editable copy of this ${
                isExperiment ? 'experiment' : 'matrix'
              } to your own files. You can access the duplicate from your private gallery.`}
              primaryAction={() => {
                onDuplicateMatrix();
                switchPopovers('duplicate', false);
              }}
              primaryActionLabel={'Duplicate'}
              secondaryAction={() => switchPopovers('duplicate', false)}
              secondaryActionLabel={'Cancel'}
            />
          }
          isOpen={openPopovers['duplicate']}
          parentRef={duplicateRef.current}
        />
        <Popover
          title="Create experiment"
          content={
            <GenericPopoverContent
              text={
                'Create a new experiment of this matrix to test different hypotheses. This will create a new file saved to your private gallery. Changes made to your current file are saved automatically.'
              }
              primaryAction={() => {
                onCreateExperiment();
                switchPopovers('experiment', false);
              }}
              primaryActionLabel={'Create'}
              secondaryAction={() => switchPopovers('experiment', false)}
              secondaryActionLabel={'Cancel'}
              {...nodeOverrideProps}
            />
          }
          isOpen={openPopovers['experiment']}
          parentRef={experimentRef.current}
        />
        <Popover
          title="Convert experiment to base matrix"
          content={
            <GenericPopoverContent
              text={
                'Create a new experiment of this matrix to test a new hypothesis. This will create a new file saved to your private gallery. Changes made to your current file are saved automatically.'
              }
              primaryAction={() => {
                handleConvertToMatrix();
                switchPopovers('convert', false);
              }}
              primaryActionLabel={'Convert'}
              secondaryAction={() => switchPopovers('experiment', false)}
              secondaryActionLabel={'Cancel'}
            />
          }
          isOpen={openPopovers['convert']}
          parentRef={convertRef.current}
        />
      </Menu>
      <Tooltip id="menu-tooltip" className="react-tooltip" />
    </>
  );
};

export default MatrixMenu;
